import { SkuParser } from '@jouzen/ecom-commons';
import { StatsigClient } from '@statsig/js-client';

import { type Partner } from '@/configs/partner';
import { logger as browserLogger } from '@/logger/logger.browser';
import { Eligibility } from '@/types/Eligibility';
import { ApiError } from '@/types/Error';
import { metricIncrement } from '@/utils/reportMetrics';

import { EligibilityItem } from '../api/getEligibilities/types';
import {
  LOGIN_METRIC_NAME,
  MetricStep,
} from '../components/ui/EmailForm/const';

export type GetEligibilitiesResponse = {
  data?: Eligibility[];
  error?: ApiError | unknown;
};

export async function getUnclaimedEligibilities(
  partner: Partner,
): Promise<GetEligibilitiesResponse> {
  try {
    if (!StatsigClient.instance().checkGate('eligibility_claiming')) {
      return {
        error: new Error('Eligibility claiming is not enabled'),
      };
    }

    const resp = await fetch('/api/getEligibilities', {
      method: 'POST',
      body: JSON.stringify({ partner }),
    });
    const responseJson = await resp.json();

    if (responseJson?.error) {
      metricIncrement(LOGIN_METRIC_NAME, {
        step: MetricStep.GET_ELIGIBILITIES_ERROR,
        partner,
      });

      browserLogger.warn('EOP Get Eligibilities error', {
        partner,
        error: responseJson?.error ?? null,
      });

      return {
        error: responseJson?.error,
      };
    }

    metricIncrement(LOGIN_METRIC_NAME, {
      step: MetricStep.GET_ELIGIBILITIES_SUCCESS,
      partner,
    });

    const filteredEligibilities = (responseJson as EligibilityItem[])?.filter(
      (eligibility) => eligibility.status === 'unclaimed',
    );

    if (filteredEligibilities.length === 0) {
      return {
        error: new Error('No applicable unclaimed eligibilities found'),
      };
    }

    return {
      data: filteredEligibilities.map((eligibility) => {
        return {
          ...eligibility,
          lineItems: eligibility.lineItems?.map((item) => {
            const parsedSku = new SkuParser(item.skuCode);
            // Set the sku if the ring selections were completed
            const sku = parsedSku.hasSize() ? item.skuCode : undefined;
            return {
              originalSku: item.skuCode,
              quantity: item.quantity,
              sku: sku,
              ...(item.price != null && { price: item.price }),
              ...(item.discount != null && { discount: item.discount }),
            };
          }),
        };
      }),
    };
  } catch (error) {
    browserLogger.warn('Error calling getUnclaimedEligibilities', {
      error,
    });
    return { error };
  }
}
